import React from 'react';
import { CheckCircle2Icon, ShieldCheckIcon, UsersRoundIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AboutPageHero } from '../components/AboutPageHero';
const reasons = [['Small groups, more presence', 'We cap group sizes to keep the trail experience personal, flexible, and kind to the places we visit.'], ['Safety before the schedule', 'Careful acclimatisation, satellite communications, proven equipment, and experienced leaders underpin every day.'], ['Leadership that is local', 'Your trip is led by people with a lifetime of mountain knowledge and direct ties to the communities along the route.'], ['Planning without surprises', 'We explain inclusions, exclusions, permits, and practical choices clearly before you reserve your place.'], ['Travel that gives back', 'We prioritise local suppliers and low-impact practices so the benefit of trekking stays close to the trail.']];
export function WhyChooseUsPage() {
  return <main className="w-full bg-[#f7f4ed] text-[#233a32]" data-id="element-723" data-mp="1equ29ellecdz">
      <AboutPageHero eyebrow="Why travel with us" title="The confidence to go further." description="Exceptional mountain travel comes from quietly excellent planning, experienced local leadership, and the freedom to be fully present." imageUrl="https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=2000&q=85" data-id="element-724" data-mp="1iqcm071oghf8" />

      <section className="mx-auto max-w-6xl px-6 py-16 sm:py-24" data-id="element-725" data-mp="22elcmcnkofcm">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.35fr] lg:gap-20" data-id="element-726" data-mp="22d0ksusn7aig">
          <div data-id="element-727" data-mp="26hqcs8k6k4y3">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9a7b3e]" data-id="element-728" data-mp="26h88cond6kf3">
              Designed around people, not volume
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl" data-id="element-729" data-mp="c37bg9bdk7hn">
              A more thoughtful way into the mountains.
            </h2>
          </div>
          <p className="max-w-2xl text-base leading-8 text-[#53645b]" data-id="element-730" data-mp="h3br5x4gky6t">
            We pair the standards of a carefully run expedition with the warmth
            of a local welcome. The result is a trek that feels seamless without
            ever feeling packaged—well supported, responsibly paced, and
            grounded in the real character of Manaslu.
          </p>
        </div>

        <div className="mt-14 divide-y divide-[#d9d0bf] border-y border-[#d9d0bf]" data-id="element-731" data-mp="1og42yykdp9fa">
          {reasons.map(([title, text], index) => <article key={title} className="grid gap-4 py-7 sm:grid-cols-[3rem_1fr_1.45fr] sm:items-start sm:gap-8" data-id="element-732" data-mp="1ycpd1yb99apz" data-mpi={index}>
              <span className="font-serif text-2xl text-[#a98b50]" data-id="element-733" data-mp="2c5onhlw6keqo">
                0{index + 1}
              </span>
              <h3 className="font-serif text-2xl" data-id="element-734" data-mp="2m1rt51q8qvid">{title}</h3>
              <p className="text-sm leading-7 text-[#53645b]" data-id="element-735" data-mp="2m29yqoysmm2y">{text}</p>
            </article>)}
        </div>
      </section>

      <section className="bg-[#233a32] px-6 py-16 text-[#f7f4ed] sm:py-20" data-id="element-736" data-mp="30ogz9mf006xq">
        <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-3" data-id="element-737" data-mp="30mdzwbryh1gc">
          <div data-id="element-738" data-mp="35m8tin348553">
            <UsersRoundIcon className="h-6 w-6 text-[#d9c78d]" aria-hidden="true" data-id="element-739" data-mp="35msaa0h14nhj" />
            <h2 className="mt-5 font-serif text-2xl" data-id="element-740" data-mp="2qsnifqu8xra5">Intentionally small</h2>
            <p className="mt-3 text-sm leading-7 text-[#d9d8cf]" data-id="element-741" data-mp="2vnja2pvkmcmq">
              Closer connections, easier decisions, and a lighter footprint.
            </p>
          </div>
          <div data-id="element-742" data-mp="2mphckkb1q247">
            <ShieldCheckIcon className="h-6 w-6 text-[#d9c78d]" aria-hidden="true" data-id="element-743" data-mp="2mq8z75mo0pic" />
            <h2 className="mt-5 font-serif text-2xl" data-id="element-744" data-mp="27xyos0by33aa">
              Prepared for the unknown
            </h2>
            <p className="mt-3 text-sm leading-7 text-[#d9d8cf]" data-id="element-745" data-mp="1yz3f2e9ozb28">
              Conservative protocols and robust support when conditions change.
            </p>
          </div>
          <div data-id="element-746" data-mp="232t22zapxkl5">
            <CheckCircle2Icon className="h-6 w-6 text-[#d9c78d]" aria-hidden="true" data-id="element-747" data-mp="1vxiii0d1ojg7" />
            <h2 className="mt-5 font-serif text-2xl" data-id="element-748" data-mp="1qxvx33n2bwwb">Clear from day one</h2>
            <p className="mt-3 text-sm leading-7 text-[#d9d8cf]" data-id="element-749" data-mp="1m7p7b0gte6ch">
              Honest advice and transparent details before any commitment.
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-16 text-center sm:py-24" data-id="element-750" data-mp="1d70q5ww4jpvd">
        <h2 className="font-serif text-3xl sm:text-4xl" data-id="element-751" data-mp="1d4xw91rupcld">
          Meet the people behind the plan.
        </h2>
        <Link to="/about/team" className="mt-7 inline-flex border-b border-[#9a7b3e] pb-2 text-sm font-semibold uppercase tracking-[0.16em] text-[#233a32] hover:text-[#9a7b3e] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9a7b3e] focus-visible:ring-offset-4" data-id="element-752" data-mp="138utbtp6d9xd">
          Explore our team
        </Link>
      </section>
    </main>;
}